<template>
  <div>
    <CRow>
      <CCol col="12">
        <div class="d-flex justify-content-between align-items-end mb-4">
          <div class="">
            <h1 class="main-header mb-1">
              <translate translate-context="collaborators.list">Collaborators</translate>
            </h1>
            <p class="text-help-dark m-0">
              <translate translate-context="collaborators.list">Create and manage user roles with specific permissions and visibility scopes
              </translate>
            </p>
          </div>
          <div class="">
            <CButton
              v-if="hasScopePerm('collaborator.export_credentials')"
              color="info"
              size="sm"
              class="mr-2"
              @click="openExportFtpModal"
            >
              <translate translate-context="collaborators.list">Export FTP</translate>
            </CButton>

            <!-- "+ Nuovo" dropdown / button -->
            <CDropdown
              v-if="newActionOptions.length > 1"
              color="primary"
              split
              size="sm"
              :disabled="allRoleLimitsExhausted"
              @click="newActionOptions[0].action()"
            >
              <template #toggler-content>
                <CIcon name="cipPlus" class="mr-1"/>
                <translate translate-context="collaborators.list">New</translate>
              </template>
              <CDropdownItem
                v-for="option in newActionOptions"
                :key="option.key"
                @click="option.action()"
              >
                <CIcon :name="option.icon" size="custom-size" :height="16" class="mr-2" v-if="option.icon"/>
                {{ option.label }}
              </CDropdownItem>
            </CDropdown>

            <CButton
              v-else-if="newActionOptions.length === 1"
              color="primary"
              size="sm"
              :disabled="allRoleLimitsExhausted"
              @click="newActionOptions[0].action()"
            >
              <CIcon name="cipPlus" size="sm" class="mr-1"/>
              <translate translate-context="collaborators.list">New</translate>
            </CButton>
          </div>
        </div>
        <CCard>
          <CCardHeader class="border-0 ml-2">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipUser01" size="custom-size" :width="18"/>
              <span class="mx-1" v-translate translate-context="collaborators">All collaborators</span>
              <span>({{ totalCollaboratorCount|number }})</span>
            </div>
            <div class="card-header-actions">
               <CInput
                v-model="searchQuery"
                :placeholder="$pgettext('collaborators.search', 'Search by email...')"
                size="sm"
                @input="onSearchInput"
              >
                <template #prepend-content>
                  <CIcon name="cilSearch" size="sm"/>
                </template>
              </CInput>
            </div>
          </CCardHeader>

            <CCardBody class="pt-0">
              <div v-if="!isReady" class="text-center py-5">
                <CSpinner size="sm" color="primary"/>
              </div>

              <div v-else-if="collaboratorList.length === 0" class="text-center py-5 text-muted">
                <CIcon name="cipUsers" size="custom-size" :height="34" class="mb-2"/>
                <p>
                  <translate translate-context="collaborators.list">No collaborators found</translate>
                </p>
              </div>

              <table class="table table-borderless" v-else>
                <CollaboratorRow
                  v-for="item in collaboratorList"
                  :key="item.id"
                  :collaborator="item"
                  :available-roles="availableRoles"
                  :scope="scope"
                  :scope-type="scopeType"
                  :is-expanded="expandedId === item.id"
                  @toggle-expand="onToggleExpand"
                  @view-permissions="showUserPermissions"
                  @add-role="onAssignRoleFromDetail"
                  @view-ftp-credentials="showFtpCredentials"
                  @reset-password="confirmResetVirtualUserPassword"
                  @edit-email="openEditEmailModal"
                  @resend-invitation="confirmResendInvitation"
                  @force-accept="confirmForceAccept"
                  @disable="confirmDisable"
                  @enable="confirmEnable"
                  @delete="confirmDelete"
                  @set-virtual="confirmSetVirtual"
                  @unset-virtual="confirmUnsetVirtual"
                  @change-account-email="openChangeAccountEmailModal"
                  @disable-role="onDisableRole"
                  @enable-role="onEnableRole"
                  @delete-role="onDeleteRole"
                  @view-role-permissions="showRolePermissions"
                />
              </table>
            </CCardBody>

            <!-- Pagination -->
            <CCardFooter v-if="totalPages > 1" class="d-flex justify-content-center py-2">
              <CPagination
                :active-page="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
              />
            </CCardFooter>

        </CCard>
      </CCol>
    </CRow>

    <!-- Modals -->
    <UserPermissionModal
      ref="userPermissionModal"
      :scope="scope"
    />

    <RolePermissionsModal
      ref="rolePermissionsModal"
      :scope="scope"
    />

    <FtpCredentialsModal
      ref="ftpCredentialsModal"
    />

    <RoleSelectionModal
      ref="roleSelectionModal"
      :available-roles="availableRoles"
      :scope-type="scopeType"
      @view-permissions="showRolePermissions"
      @assign-roles="onAssignRoles"
    />

    <EditEmailModal
      ref="editEmailModal"
      :scope="scope"
      @updated="onEmailUpdated"
    />

    <ExportFtpModal
      ref="exportFtpModal"
      :scope-type="scopeType"
    />

    <VirtualUserCredentialsModal
      ref="virtualUserCredentialsModal"
    />

    <ExportVirtualUsersModal
      ref="exportVirtualUsersModal"
      @passwords-reset="onPasswordsReset"
    />

    <InviteCollaboratorModal
      ref="inviteCollaboratorModal"
      :scope="scope"
      :available-roles="availableRoles"
      :limits-usage="limitsUsage"
      :can-bypass-limits="canBypassLimits"
      @invited="onCollaboratorsChanged"
    />

    <AssignCollaboratorModal
      ref="assignCollaboratorModal"
      :scope="scope"
      :available-roles="availableRoles"
      :limits-usage="limitsUsage"
      :can-bypass-limits="canBypassLimits"
      @assigned="onCollaboratorsChanged"
    />

    <VirtualUserModal
      ref="virtualUserModal"
      :scope="scope"
      :available-roles="availableRoles"
      :limits-usage="limitsUsage"
      :can-bypass-limits="canBypassLimits"
      @virtual-users-created="onVirtualUsersCreated"
    />

    <ChangeAccountEmailModal
      ref="changeAccountEmailModal"
      @updated="onAccountEmailUpdated"
    />
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import CollaboratorRow from './CollaboratorRow.vue'
import UserPermissionModal from './UserPermissionModal.vue'
import RolePermissionsModal from './RolePermissionsModal.vue'
import FtpCredentialsModal from './FtpCredentialsModal.vue'
import RoleSelectionModal from './RoleSelectionModal.vue'
import EditEmailModal from './EditEmailModal.vue'
import ExportFtpModal from './ExportFtpModal.vue'
import VirtualUserCredentialsModal from './VirtualUserCredentialsModal.vue'
import ExportVirtualUsersModal from './ExportVirtualUsersModal.vue'
import InviteCollaboratorModal from './InviteCollaboratorModal.vue'
import AssignCollaboratorModal from './AssignCollaboratorModal.vue'
import VirtualUserModal from './VirtualUserModal.vue'
import ChangeAccountEmailModal from './ChangeAccountEmailModal.vue'
import PButton from "@/components/PButton.vue";
import ConfigScope from "@/domain/core/constant/configScope"

export default {
  name: 'Collaborators',

  components: {
    PButton,
    CollaboratorRow,
    UserPermissionModal,
    RolePermissionsModal,
    FtpCredentialsModal,
    RoleSelectionModal,
    EditEmailModal,
    ExportFtpModal,
    VirtualUserCredentialsModal,
    ExportVirtualUsersModal,
    InviteCollaboratorModal,
    AssignCollaboratorModal,
    VirtualUserModal,
    ChangeAccountEmailModal
  },

  props: {
    scopeType: {
      type: String,
      required: true,
      validator: v => ['global', 'contract-manager', 'pica-service'].includes(v)
    },
    scopeId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      isReady: false,
      searchQuery: '',
      searchDebounce: null,
      expandedId: null
    }
  },

  computed: {
    ...mapGetters('collaborators', [
      'collaborators',
      'availableRoles',
      'totalCollaboratorCount',
      'hasVirtualUsers',
      'limitsUsage',
      'canBypassLimits',
      'allRoleLimitsExhausted'
    ]),

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId
      }
    },

    collaboratorList() {
      return this.collaborators?.results || []
    },

    currentPage() {
      return this.collaborators?.pagination?.page || this.collaborators?.pagination?.page_number || 1
    },

    totalPages() {
      const total = this.collaborators?.pagination?.total || 0
      const pageSize = this.collaborators?.pagination?.page_size || 20
      return Math.ceil(total / pageSize) || 1
    },

    newActionOptions() {
      const options = []
      if (this.hasScopePerm('collaborator.virtual')) {
        options.push({
          key: 'virtual',
          label: this.$pgettext('collaborators.new', 'Virtual user'),
          icon: 'cipRobot',
          action: () => this.openVirtualUserModal()
        })
      }
      if (this.hasScopePerm('collaborator.invite')) {
        options.push({
          key: 'invite',
          label: this.$pgettext('collaborators.new', 'Invite collaborators'),
          icon: 'cipEmail',
          action: () => this.openInviteModal()
        })
      }
      if (this.hasScopePerm('collaborator.assign')) {
        options.push({
          key: 'assign',
          label: this.$pgettext('collaborators.new', 'Assign collaborators'),
          icon: 'cipUsersPlus',
          action: () => this.openAssignModal()
        })
      }
      return options
    }
  },

  async mounted() {
    try {
      await this.setScope(this.scope)

      if (this.scopeType === 'pica-service') {
        await this.fetchConfig({
          type: 'collaborators_pica_service',
          id: this.scopeId,
          scope: ConfigScope.PICA_SERVICE
        })
      } else if (this.scopeType === 'contract-manager') {
        await this.fetchConfig({
          type: 'collaborators_contract_manager',
          id: this.scopeId
        })
      } else {
        await this.fetchConfig({
          type: 'collaborators_global'
        })
      }

      await this.fetchAvailableRoles()

      if (this.scopeType === 'pica-service') {
        await this.fetchLimitsUsage()
      }
    } catch (error) {
      this.notifyError(this.$pgettext('collaborators.notification', 'Failed to load data'))
    } finally {
      this.isReady = true
    }
  },

  beforeDestroy() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce)
    }
  },

  methods: {
    ...mapActions('collaborators', [
      'setScope',
      'fetchCollaborators',
      'fetchAvailableRoles',
      'fetchLimitsUsage',
      'resendInvitation',
      'forceAcceptInvitation',
      'fetchCollaboratorDetail',
      'fetchRolePermissions',
      'updateFilters',
      'changePage',
      'assignRoleToCollaborator',
      'removeRoleFromCollaborator',
      'disableRoleForCollaborator',
      'enableRoleForCollaborator'
    ]),

    ...mapActions('login', [
      'fetchConfig'
    ]),

    ...mapActions('customerSuccess', [
      'setVirtualUser',
      'unsetVirtualUser',
      'changeAccountEmail'
    ]),

    // --- Expand ---
    onToggleExpand(collaboratorId) {
      this.expandedId = this.expandedId === collaboratorId ? null : collaboratorId
    },

    // --- Search ---
    onSearchInput() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      this.searchDebounce = setTimeout(() => {
        this.updateFilters({
          search: this.searchQuery,
          page: 1
        })
      }, 400)
    },

    // --- Pagination ---
    onPageChange(page) {
      this.expandedId = null
      this.changePage(page)
    },

    // --- Role actions (immediate) ---
    async onAssignRoles(collaboratorId, roleIds) {
      try {
        for (const roleId of roleIds) {
          await this.assignRoleToCollaborator({collaboratorId, roleId})
        }
        this.notifySuccess(this.$pgettext('collaborators.notification', 'Roles assigned successfully'))
      } catch (e) {
        this.notifyError(this.$pgettext('collaborators.notification', 'Failed to assign roles'))
      }
    },

    async onDisableRole({collaboratorId, roleId}) {
      // Already confirmed in CollaboratorRoleDetail, execute immediately
      try {
        await this.disableRoleForCollaborator({collaboratorId, roleId})
        this.notifySuccess(this.$pgettext('collaborators.notification', 'Role disabled'))
      } catch (e) {
        this.notifyError(this.$pgettext('collaborators.notification', 'Failed to disable role'))
      }
    },

    async onEnableRole({collaboratorId, roleId}) {
      // Already confirmed in CollaboratorRoleDetail, execute immediately
      try {
        await this.enableRoleForCollaborator({collaboratorId, roleId})
        this.notifySuccess(this.$pgettext('collaborators.notification', 'Role enabled'))
      } catch (e) {
        this.notifyError(this.$pgettext('collaborators.notification', 'Failed to enable role'))
      }
    },

    async onDeleteRole({collaboratorId, roleId}) {
      // Already confirmed in CollaboratorRoleDetail, execute immediately
      try {
        await this.removeRoleFromCollaborator({collaboratorId, roleId})
        this.notifySuccess(this.$pgettext('collaborators.notification', 'Role removed'))
      } catch (e) {
        this.notifyError(this.$pgettext('collaborators.notification', 'Failed to remove role'))
      }
    },

    onAssignRoleFromDetail(collaborator) {
      const currentRoleIds = new Set((collaborator.roles || []).map(r => r.id))
      const assignable = this.availableRoles.filter(r => !currentRoleIds.has(r.id))

      if (assignable.length === 0) {
        this.notifyError(this.$pgettext('collaborators.notification', 'No more roles available to assign'))
        return
      }

      this.openRoleModal(collaborator)
    },

    // --- Modals ---
    openInviteModal() {
      this.$refs.inviteCollaboratorModal.open()
    },

    openAssignModal() {
      this.$refs.assignCollaboratorModal.open()
    },

    openVirtualUserModal() {
      this.$refs.virtualUserModal.open()
    },

    openRoleModal(collaborator) {
      const roles = collaborator.roles || []
      const isAllInherited = roles.length > 0 && roles.every(r => r.is_inherited)
      this.$refs.roleSelectionModal.show({
        title: this.$pgettext('collaborators.modal', 'Add Role'),
        currentRoles: roles,
        isInheritedUser: isAllInherited,
        collaboratorId: collaborator.id
      })
    },

    openEditEmailModal(collaborator) {
      this.$refs.editEmailModal.show(collaborator)
    },

    openExportFtpModal() {
      this.$refs.exportFtpModal.open()
    },

    openChangeAccountEmailModal(collaborator) {
      this.$refs.changeAccountEmailModal.show(collaborator)
    },

    async showUserPermissions(user) {
      this.$refs.userPermissionModal.show(user)
    },

    async showRolePermissions(role) {
      const permissions = await this.fetchRolePermissions(role.id)
      this.$refs.rolePermissionsModal.show(permissions, role)
    },

    async showFtpCredentials(collaborator) {
      try {
        const detail = await this.fetchCollaboratorDetail(collaborator.id)
        this.$refs.ftpCredentialsModal.show(collaborator.email, detail?.ftp_credentials)
      } catch (e) {
        this.notifyError(this.$pgettext('collaborators.notification', 'Failed to load FTP credentials'))
      }
    },

    // --- Confirmations ---
    confirmDisable(item) {
      const activeDirectRoles = (item.roles || []).filter(r => !r.is_inherited && r.is_active !== false)
      if (activeDirectRoles.length === 0) return

      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Disable'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to disable this collaborator?'),
        cb_confirm: async () => {
          try {
            await Promise.all(activeDirectRoles.map(role =>
              this.disableRoleForCollaborator({collaboratorId: item.id, roleId: role.id})
            ))
            this.notifySuccess(this.$pgettext('collaborators.notification', 'Collaborator disabled'))
          } catch (e) {
            this.notifyError(this.$pgettext('collaborators.notification', 'Failed to disable collaborator'))
          }
        }
      })
    },

    confirmEnable(item) {
      const disabledDirectRoles = (item.roles || []).filter(r => !r.is_inherited && r.is_active === false)
      if (disabledDirectRoles.length === 0) return

      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Enable'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to enable this collaborator?'),
        cb_confirm: async () => {
          try {
            await Promise.all(disabledDirectRoles.map(role =>
              this.enableRoleForCollaborator({collaboratorId: item.id, roleId: role.id})
            ))
            this.notifySuccess(this.$pgettext('collaborators.notification', 'Collaborator enabled'))
          } catch (e) {
            this.notifyError(this.$pgettext('collaborators.notification', 'Failed to enable collaborator'))
          }
        }
      })
    },

    confirmDelete(item) {
      const directRoles = (item.roles || []).filter(r => !r.is_inherited)
      if (directRoles.length === 0) return

      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Delete'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to delete this collaborator?'),
        color: 'danger',
        cb_confirm: async () => {
          try {
            await Promise.all(directRoles.map(role =>
              this.removeRoleFromCollaborator({collaboratorId: item.id, roleId: role.id})
            ))
            this.expandedId = null
            this.notifySuccess(this.$pgettext('collaborators.notification', 'Collaborator deleted'))
          } catch (e) {
            this.notifyError(this.$pgettext('collaborators.notification', 'Failed to delete collaborator'))
          }
        }
      })
    },

    confirmForceAccept(item) {
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Force Accept Invitation'),
        message: this.$pgettext('collaborators.confirm', 'Are you sure you want to force accept this invitation? This will create a user account with the provided email address.'),
        cb_confirm: async () => {
          try {
            await this.forceAcceptInvitation(item.id)
            this.notifySuccess(this.$pgettext('collaborators.notification', 'Invitation forcefully accepted'))
          } catch (e) {
            if (e.data?.code === 'invalid_collaborator_status') {
              this.notifyError(this.$pgettext('collaborators.notification', 'Can only force accept pending invitations'))
            } else {
              this.notifyError(this.$pgettext('collaborators.notification', 'Failed to force accept invitation'))
            }
          }
        }
      })
    },

    confirmResendInvitation(item) {
      const emailMessage = this.$pgettext('collaborators.confirm', 'Send invitation email to') + ' ' + item.email + '?'
      this.confirm({
        title: this.$pgettext('collaborators.confirm', 'Resend invitation'),
        message: emailMessage,
        cb_confirm: async () => {
          try {
            await this.resendInvitation(item.id)
            this.notifySuccess(this.$pgettext('collaborators.notification', 'Invitation email sent to') + ' ' + item.email)
          } catch (e) {
            this.notifyError(this.$pgettext('collaborators.notification', 'Failed to send invitation email'))
          }
        }
      })
    },

    confirmResetVirtualUserPassword(item) {
      this.$refs.exportVirtualUsersModal.open(item.id)
    },

    confirmSetVirtual(item) {
      this.confirm({
        title: this.$pgettext('collaborators.admin', 'Set as Virtual User'),
        message: this.$pgettext('collaborators.admin', 'This user will need to use a password to login instead of email OTP. This affects the entire account.'),
        cb_confirm: async () => {
          try {
            await this.setVirtualUser(item.user_id)
            this.notifySuccess(this.$pgettext('collaborators.admin', 'User set as virtual'))
            await this.fetchCollaborators()
          } catch (e) {
            this.notifyError(this.$pgettext('collaborators.admin', 'Failed to set user as virtual'))
          }
        }
      })
    },

    confirmUnsetVirtual(item) {
      this.confirm({
        title: this.$pgettext('collaborators.admin', 'Set as Regular User'),
        message: this.$pgettext('collaborators.admin', 'This user will need a real email to receive login OTP. This affects the entire account.'),
        cb_confirm: async () => {
          try {
            await this.unsetVirtualUser(item.user_id)
            this.notifySuccess(this.$pgettext('collaborators.admin', 'User set as regular'))
            await this.fetchCollaborators()
          } catch (e) {
            this.notifyError(this.$pgettext('collaborators.admin', 'Failed to set user as regular'))
          }
        }
      })
    },

    // --- Callbacks ---
    async onEmailUpdated() {
      await this.fetchCollaborators()
    },

    async onCollaboratorsChanged() {
      await this.fetchCollaborators()
      if (this.scopeType === 'pica-service') {
        await this.fetchLimitsUsage()
      }
    },

    onVirtualUsersCreated(credentials) {
      this.$refs.virtualUserCredentialsModal.show(credentials)
    },

    onPasswordsReset(credentials) {
      this.$refs.virtualUserCredentialsModal.show(credentials)
    },

    async onAccountEmailUpdated() {
      await this.fetchCollaborators()
    }
  }
}
</script>

<style scoped>
.search-box {
  max-width: 400px;
}
</style>
